import type { NavLayout } from '@khaddi/contracts';
import { Children, createContext, isValidElement, useContext, useMemo, type ReactNode } from 'react';
import { Pressable, ScrollView, useWindowDimensions, View, type StyleProp, type ViewStyle } from 'react-native';
import { screenSizeFor, type ScreenSize } from '../screenSize';
import { useNavLayout } from '../session';
import { colors, BRAND_GRADIENT, CONTENT_MAX_WIDTH, HIT } from '../tokens';
import { Icon } from './Icon';
import { BottomNav, SideNav, TopNav, type NavProps } from './nav';
import { Text } from './primitives';

type LayoutContextValue = { defaultNav: NavLayout };

const LayoutContext = createContext<LayoutContextValue>({ defaultNav: 'top' });

export type AppFrameProps = {
  children: ReactNode;
  background?: string;
  /** Nav layout on wide screens until the user picks one under "Giao diện". */
  defaultNav: NavLayout;
};

/**
 * Phones get the 390 px design in a column of at most 480 px; from the tablet
 * breakpoint the app fills the window. Only styles change across the breakpoint,
 * so resizing never remounts the screen (or loses a half-filled form).
 */
export const AppFrame = ({ children, background = colors.background, defaultNav }: AppFrameProps) => {
  const { width } = useWindowDimensions();
  const wide = screenSizeFor(width) !== 'mobile';
  const value = useMemo(() => ({ defaultNav }), [defaultNav]);
  return (
    <LayoutContext.Provider value={value}>
      <View style={{ flex: 1, minHeight: '100vh' as unknown as number, backgroundColor: wide ? background : '#D3E4EE', alignItems: 'center' }}>
        <View style={{ flex: 1, width: '100%', maxWidth: wide ? undefined : 480, backgroundColor: background, position: 'relative' }}>{children}</View>
      </View>
    </LayoutContext.Provider>
  );
};

export type Layout = { size: ScreenSize; navLayout: NavLayout; wide: boolean };

export const useLayout = (): Layout => {
  const { width } = useWindowDimensions();
  const { defaultNav } = useContext(LayoutContext);
  const navLayout = useNavLayout(defaultNav);
  const size = screenSizeFor(width);
  return { size, navLayout, wide: size !== 'mobile' };
};

export type AppNavConfig = NavProps & {
  /** Sub-screens (product, checkout, tracking…) keep their back button on phones and only show the nav on wide screens. */
  wideOnly?: boolean;
};

export type ScreenProps = {
  children: ReactNode;
  background?: string;
  /** Sticky bar at the bottom (checkout CTA); on phones it sits above the bottom nav. */
  footer?: ReactNode;
  /** The app's main nav: bottom bar on phones, top or side nav on wide screens. */
  nav?: AppNavConfig;
  /** Content column width on wide screens. */
  maxWidth?: number;
  contentStyle?: StyleProp<ViewStyle>;
};

export const Screen = ({ children, background = colors.background, footer, nav, maxWidth = CONTENT_MAX_WIDTH, contentStyle }: ScreenProps) => {
  const { size, navLayout, wide } = useLayout();
  const bottomNav = nav && !wide && !nav.wideOnly ? <BottomNav items={nav.items} active={nav.active} label={nav.label} /> : null;
  const sticky = footer || bottomNav;
  const column = wide ? ({ width: '100%', maxWidth, alignSelf: 'center' } as const) : null;
  const side = Boolean(nav && wide && navLayout === 'side');
  return (
    <View style={{ flex: 1, flexDirection: side ? 'row' : 'column', backgroundColor: background }}>
      {nav && wide && navLayout === 'top' ? <TopNav items={nav.items} active={nav.active} label={nav.label} size={size} /> : null}
      {nav && side ? <SideNav items={nav.items} active={nav.active} label={nav.label} size={size} /> : null}
      <View style={{ flex: 1, minWidth: 0 }}>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={[{ paddingBottom: sticky ? 120 : 24 }, column, contentStyle]}>
          {children}
        </ScrollView>
        {sticky ? (
          <View style={{ position: 'sticky' as unknown as 'absolute', bottom: 0, left: 0, right: 0 }}>
            {footer ? <View style={column}>{footer}</View> : null}
            {bottomNav}
          </View>
        ) : null}
      </View>
    </View>
  );
};

/** Rows of `columns` equal cells; a short last row keeps its cells at full-row widths. */
export const Grid = ({ columns, gap = 12, children }: { columns: number; gap?: number; children: ReactNode }) => {
  const cells = Children.toArray(children);
  const rows: ReactNode[][] = [];
  for (let i = 0; i < cells.length; i += columns) rows.push(cells.slice(i, i + columns));
  const keyOf = (cell: ReactNode, index: number) => (isValidElement(cell) && cell.key !== null ? cell.key : index);
  return (
    <View style={{ gap }}>
      {rows.map((row, r) => (
        <View key={row.map(keyOf).join() || r} style={{ flexDirection: 'row', gap }}>
          {row.map((cell, c) => (
            <View key={keyOf(cell, c)} style={{ flex: 1, minWidth: 0 }}>
              {cell}
            </View>
          ))}
          {Array.from({ length: columns - row.length }, (_, i) => (
            <View key={`empty-${i}`} style={{ flex: 1 }} />
          ))}
        </View>
      ))}
    </View>
  );
};

export const FooterBar = ({ children, style }: { children: ReactNode; style?: StyleProp<ViewStyle> }) => (
  <View style={[{ backgroundColor: colors.surface, borderTopWidth: 1, borderTopColor: colors.border, paddingTop: 12, paddingHorizontal: 16, paddingBottom: 24, gap: 10 }, style]}>
    {children}
  </View>
);

export type TopBarProps = { title: string; onBack?: () => void; right?: ReactNode; background?: string };

export const TopBar = ({ title, onBack, right, background = colors.surface }: TopBarProps) => (
  <View style={{ height: 60, backgroundColor: background, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 8, borderBottomWidth: background === colors.surface ? 1 : 0, borderBottomColor: colors.border }}>
    {onBack ? (
      <Pressable accessibilityRole="button" accessibilityLabel="Quay lại" onPress={onBack} style={{ width: HIT, height: HIT, alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="chevronLeft" size={22} strokeWidth={2.2} color={colors.text} />
      </Pressable>
    ) : (
      <View style={{ width: 8 }} />
    )}
    <Text variant="title" style={{ fontSize: 20, flex: 1 }} numberOfLines={1}>
      {title}
    </Text>
    {right}
  </View>
);

/** Background for large brand surfaces (logo blue fading lighter); spread into a View style. */
export const brandSurface = { backgroundColor: colors.sky, backgroundImage: BRAND_GRADIENT } satisfies ViewStyle;

export const SectionHeader = ({ title, action }: { title: string; action?: { label: string; onPress: () => void } }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
    <Text variant="title" style={{ fontSize: 20 }}>
      {title}
    </Text>
    {action ? (
      <Pressable accessibilityRole="link" onPress={action.onPress} style={{ paddingVertical: 6 }}>
        <Text variant="bodyStrong" color={colors.primaryText} style={{ fontSize: 13 }}>
          {action.label}
        </Text>
      </Pressable>
    ) : null}
  </View>
);
